import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Bot, RotateCcw } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast as sonnerToast } from "sonner";
import { toast } from "@/hooks/use-toast";
import type { Msg } from "./chatbot/types";
import { streamChat } from "./chatbot/streamChat";
import ChatMessage from "./chatbot/ChatMessage";
import ChatInput from "./chatbot/ChatInput";
import QuickReplies from "./chatbot/QuickReplies";
import ChatTeaser from "./chatbot/ChatTeaser";
import TypingIndicator from "./chatbot/TypingIndicator";

const STORAGE_KEY = "chatbot_engaged";
const TEASER_SHOWN_KEY = "chatbot_teaser_shown";

const Chatbot = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(() => sessionStorage.getItem(TEASER_SHOWN_KEY) === "true");
  const [engaged, setEngaged] = useState(() => sessionStorage.getItem(STORAGE_KEY) === "true");
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Mark as engaged when user opens chat
  const markEngaged = useCallback(() => {
    setEngaged(true);
    sessionStorage.setItem(STORAGE_KEY, "true");
  }, []);

  // Smart proactivity: 50% scroll + 5-10s inactivity + no contact/menu click
  useEffect(() => {
    if (open || teaserDismissed || engaged || showTeaser) return;

    let scrolledEnough = false;
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    let triggered = false;

    const trigger = () => {
      if (triggered || open || teaserDismissed) return;
      triggered = true;
      setShowTeaser(true);
      setTeaserDismissed(true);
      sessionStorage.setItem(TEASER_SHOWN_KEY, "true");

      // On subpages, also show a subtle toast notification
      if (!isHome) {
        const msg = lang === "sl"
          ? "Imate vprašanje? Naš AI pomočnik vam lahko pomaga."
          : "Have a question? Our AI assistant can help.";
        sonnerToast(msg, { duration: 5000 });
      }
    };

    const resetInactivity = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (!scrolledEnough || triggered) return;
      // Random 5-10s delay for natural feel
      const delay = 5000 + Math.random() * 5000;
      inactivityTimer = setTimeout(trigger, delay);
    };

    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPercent >= 0.5) {
        scrolledEnough = true;
        resetInactivity();
      }
    };

    const handleActivity = () => {
      resetInactivity();
    };

    // Check if user clicked contact or opened menu — if so, don't trigger
    const handleContactClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href*="contact"], a[href*="#contact"], button[aria-label*="menu"], button[aria-label*="Toggle"]');
      if (anchor) {
        triggered = true; // suppress teaser
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity, { passive: true });
    window.addEventListener("click", handleContactClick, { capture: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleContactClick, { capture: true });
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [open, teaserDismissed, engaged, showTeaser, isHome, lang]);

  useEffect(() => {
    if (open) setShowTeaser(false);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleOpen = useCallback(() => {
    markEngaged();
    setOpen(true);
  }, [markEngaged]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;
    setInput("");
    const userMsg: Msg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    let assistantSoFar = "";
    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsert,
        onDone: () => setLoading(false),
        onError: (msg) => {
          toast({ title: lang === "sl" ? "Napaka" : "Error", description: msg, variant: "destructive" });
          setLoading(false);
        },
      });
    } catch {
      toast({
        title: lang === "sl" ? "Napaka" : "Error",
        description: lang === "sl" ? "Prišlo je do napake. Poskusite znova." : "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  }, [loading, messages, lang]);

  const handleSend = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  const dismissTeaser = () => {
    setTeaserDismissed(true);
    setShowTeaser(false);
    sessionStorage.setItem(TEASER_SHOWN_KEY, "true");
    markEngaged();
  };

  const title = lang === "sl" ? "Kočevar AI pomočnik" : "Kočevar AI assistant";
  const placeholder = lang === "sl" ? "Vprašajte o uporovnem varjenju…" : "Ask about resistance welding…";

  // Context-aware teaser text
  const teaserText = isHome
    ? (lang === "sl" ? "Pozdravljeni! Vam lahko pomagam? 👋" : "Hello! Can I help you? 👋")
    : (lang === "sl" ? "Imate vprašanje o tej temi? 💡" : "Have a question about this? 💡");

  const showBadge = showTeaser && !teaserDismissed;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <div className="fixed bottom-24 right-6 z-50 flex items-end gap-3">
          {showBadge && (
            <ChatTeaser
              text={teaserText}
              onOpen={() => {
                dismissTeaser();
                // On subpages: only show teaser, never auto-open chat
                if (isHome) {
                  handleOpen();
                } else {
                  markEngaged();
                  setOpen(true);
                }
              }}
              onDismiss={dismissTeaser}
            />
          )}
          <button
            onClick={handleOpen}
            className="chatbot-fab relative w-14 h-14 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
            aria-label={lang === "sl" ? "Odpri klepet" : "Open chat"}
          >
            <MessageCircle className="w-6 h-6" />
            {showBadge && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold flex items-center justify-center animate-scale-in shadow-sm">
                1
              </span>
            )}
          </button>
        </div>
      )}

      {/* Chat window */}
      {open && (
        <div className="chatbot-window fixed bottom-20 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] rounded-xl border border-border shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground shrink-0">
            <Bot className="w-5 h-5" />
            <span className="font-heading font-bold text-sm flex-1">{title}</span>
            {messages.length > 0 && (
              <button
                onClick={() => { setMessages([]); setLoading(false); }}
                className="hover:opacity-80 transition-opacity mr-1"
                aria-label={lang === "sl" ? "Nov pogovor" : "New chat"}
                title={lang === "sl" ? "Nov pogovor" : "New chat"}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setOpen(false)} className="hover:opacity-80 transition-opacity">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && (
              <QuickReplies lang={lang} onSelect={sendMessage} />
            )}
            {messages.map((m, i) => (
              <ChatMessage key={i} message={m} />
            ))}
            {loading && messages[messages.length - 1]?.role !== "assistant" && (
              <TypingIndicator />
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <ChatInput
            ref={inputRef}
            value={input}
            onChange={setInput}
            onSend={handleSend}
            placeholder={placeholder}
            disabled={loading}
          />
        </div>
      )}
    </>
  );
};

export default Chatbot;
